import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './client'

export interface InboxItem {
  id: number
  status: string
  eventType: string | null
  source: string
  searchTerm: string | null
  reasoning: string | null
  expectedEffect: { kind?: string; suggestedBid?: number; toStage?: string } | null
  suggestedBid: string | null
  bidAtEventTime: string | null
  createdAt: string
  keywordText: string | null
  campaignName: string | null
  targetAdGroupName: string | null
  // ANZEIGE-ONLY: geplanter Kampagnenname eines portfolio_create_campaign-Drafts
  // (Backend-Alias aus metrics.generatedName). Fliesst bewusst NICHT in
  // isActionable() ein — die Kampagne existiert noch nicht, daher fail-closed
  // gesperrt (siehe groupItems.ts).
  draftTargetName: string | null
  impactSpend: number
  amazonError: string | null
}

export interface InboxSummary {
  pendingCount: number
  draftCount: number
  appliedYesterday: number
  failedApplies: number
  // Spine-3-Integration (Cross-Check #2/#4): pruefbeduerftige Apply-Versuche mit
  // unbekanntem Amazon-Ausgang (Status 'reconcile_required', Fenster I). Getrennt
  // von failedApplies (approved+error = retrybar) — brauchen Operator-Reconciliation.
  reconcileRequiredCount: number
  // CLEANUP-INBOX (Scheibe N): offene Pruefmarker des 0-Impression-Cleanups. EINE
  // ANDERE SACHE als reconcileRequiredCount, obwohl beide „pruefbeduerftig" heissen:
  // diese liegen in `cleanup_events`, erscheinen NICHT im Tab „Pruefen" und werden
  // nicht per approve/reject, sondern in der Cleanup-Ansicht quittiert. Darum ein
  // eigenes Feld und ein eigener Weg — nicht addieren.
  cleanupReconcileCount: number
}

// 'reconcile_required' (Fenster I): der Operator loest diese ueber approve
// ("Amazon geprueft, kein Write -> erneut freigeben") oder reject auf; das Backend
// akzeptiert beide Aktionen (OPERATOR_DECIDABLE_STATUSES).
export type InboxStatus = 'pending' | 'draft' | 'approved' | 'reconcile_required'

export interface SyncRunSummary {
  id: number
  profileId: number
  syncType: string
  status: 'running' | 'completed' | 'failed'
  errors: string[] | null
  startedAt: string
}

// GLB-8: der SERVER-seitige Safety-State der Datenaufnahme, je (profileId,
// syncType) und ohne Ausschnitt. Er ist die Quelle des Bedien-Gates; die
// Ableitung in `pages/inbox/alerts.ts` bleibt fuer die Alert-KARTEN zustaendig
// (sie braucht die Run-Objekte), ist aber nicht mehr die einzige Grundlage der
// Sperre — genau das war das 100-Run-Fenster: `useRecentSyncRuns` unten kann nur
// die neuesten 100 Laeufe sehen (Deckel in `sync/routes.ts`), ein blockierender
// Lauf jenseits davon verschwand daraus still.
export type SyncSafetyBlockReason = 'reconciled_stale' | 'stalled_running' | 'no_clean_sync'

export interface SyncSafetyBlock {
  profileId: number
  syncType: string | null
  reason: SyncSafetyBlockReason
  since: string | null
  runId: number | null
}

export interface SyncSafetyState {
  blocked: SyncSafetyBlock[]
  blockedProfileIds: number[]
  /** im Profilkreis (aktive Profile) existiert kein einziger sauberer Lauf */
  historyEmpty: boolean
  checkedAt: string
}

const KEYS = {
  list: (status: string) => ['inbox', status] as const,
  summary: ['inbox', 'summary'] as const,
  syncRuns: ['inbox', 'syncRuns'] as const,
  safetyState: ['inbox', 'syncSafetyState'] as const,
}

export function useInbox(status: InboxStatus) {
  return useQuery({
    queryKey: KEYS.list(status),
    queryFn: () => api.get<{ items: InboxItem[]; total: number }>(`/inbox/?status=${status}&limit=100`),
    refetchInterval: 60_000,
  })
}

export function useInboxSummary() {
  return useQuery({
    queryKey: KEYS.summary,
    queryFn: () => api.get<{ summary: InboxSummary }>('/inbox/summary/'),
    refetchInterval: 60_000,
  })
}

// Letzte Sync-Laeufe fuer die Alerts-Zone (kaputte Datenaufnahme sichtbar machen).
// limit=100 deckt mehrere Profile x Sync-Typen im Morgenfenster ab; die exakte
// "letzter Run je (profileId, syncType)"-Abfrage ist eine spaetere Verfeinerung (Handoff).
export function useRecentSyncRuns() {
  return useQuery({
    queryKey: KEYS.syncRuns,
    queryFn: () => api.get<{ runs: SyncRunSummary[] }>('/sync/runs/?limit=100'),
    refetchInterval: 60_000,
  })
}

// GLB-8: Server-Zustand des Bedien-Gates. Dieselbe Aktualisierungsfrequenz wie
// die uebrigen Sicherheitssignale — ein Ladefehler MUSS in der Oberflaeche
// fail-closed sperren (InboxPage: `signalsUnavailable`), nicht durchlassen.
export function useSyncSafetyState() {
  return useQuery({
    queryKey: KEYS.safetyState,
    queryFn: () => api.get<SyncSafetyState>('/sync/safety-state/'),
    refetchInterval: 60_000,
  })
}

function useInboxInvalidation() {
  const qc = useQueryClient()
  return () => {
    void qc.invalidateQueries({ queryKey: ['inbox'] })
    // Inbox-Aktionen laufen ueber die Promotions-Routen — dieselben Caches wie
    // die Promotions-Seite invalidieren (Keys: promotionEvents / promotionStats),
    // sonst zeigt die Promotions-Ansicht bis zum Interval-Refetch veraltete Stati.
    void qc.invalidateQueries({ queryKey: ['promotionEvents'] })
    void qc.invalidateQueries({ queryKey: ['promotionStats'] })
  }
}

export function useApproveEvent() {
  const invalidate = useInboxInvalidation()
  return useMutation({
    mutationFn: ({ id, note }: { id: number; note?: string }) =>
      api.post(`/promotions/events/${id}/approve`, note ? { note } : undefined),
    onSettled: invalidate,
  })
}

export function useRejectEvent() {
  const invalidate = useInboxInvalidation()
  return useMutation({
    mutationFn: ({ id, note }: { id: number; note?: string }) =>
      api.post(`/promotions/events/${id}/reject`, note ? { note } : undefined),
    onSettled: invalidate,
  })
}

export function useApplyEvent() {
  const invalidate = useInboxInvalidation()
  return useMutation({
    mutationFn: ({ id }: { id: number }) => api.post(`/promotions/events/${id}/apply`),
    onSettled: invalidate,
  })
}

export function usePromoteDraft() {
  const invalidate = useInboxInvalidation()
  return useMutation({
    mutationFn: ({ id }: { id: number }) => api.post(`/promotions/events/${id}/promote-draft`),
    onSettled: invalidate,
  })
}

export function useBatchDecision() {
  const invalidate = useInboxInvalidation()
  return useMutation({
    mutationFn: ({ action, ids }: { action: 'approve' | 'reject'; ids: number[] }) =>
      api.post('/promotions/events/batch/', { action, ids }),
    onSettled: invalidate,
  })
}
